import { useState } from 'react'
import { Link } from 'react-router-dom'
import BookingModal from '../../components/BookingModal'
import FontAwesomeIcon from '../../components/FontAwesomeIcon'
import FAQSection from '../../components/FAQSection'

export default function GoldenMonkeyTracking() {
  const [isModalOpen, setIsModalOpen] = useState(false)

  const faqs = [
    {
      question: "How difficult is the trek?",
      answer: "The trek is moderate and generally easier than gorilla trekking. You'll hike through bamboo forest at altitudes between 2,500-3,500 meters on uneven, sometimes muddy terrain. Trek duration varies from 30 minutes to 4 hours depending on where the monkeys are located. Most visitors with reasonable fitness manage comfortably. The terrain is less steep than gorilla areas, and the altitude, while noticeable, is manageable for most people."
    },
    {
      question: "Will I definitely see the golden monkeys?",
      answer: "Yes, success rates exceed 90%. The habituated troops are tracked daily by experienced rangers who know their ranging patterns, feeding areas, and sleeping sites. Trackers use GPS data and fresh signs to locate them quickly. The real question isn't whether you'll see them, but how much amazing behavior you'll witness during your hour with them."
    },
    {
      question: "Can I combine golden monkey tracking with gorilla trekking?",
      answer: "Absolutely! This is one of the most popular combinations. Many visitors do gorilla trekking one day and golden monkey tracking the next for a complete primate experience in Mgahinga. The two activities complement each other perfectly—gorillas offer slow, contemplative encounters while golden monkeys provide energetic, playful observations. You can also combine with volcano climbing or the Batwa cultural trail for a comprehensive Mgahinga adventure."
    },
    {
      question: "What is the minimum age requirement?",
      answer: "The minimum age is 12 years old. This makes golden monkey tracking more accessible for families compared to gorilla trekking (minimum 15 years). However, children must be able to hike for several hours on uneven terrain and maintain quiet behavior during the monkey observation period."
    },
    {
      question: "What's the best time of year for golden monkey tracking?",
      answer: "Golden monkey tracking is available year-round, with each season offering advantages. Dry seasons (June-September, December-February) provide easier hiking conditions and more comfortable weather. Wet seasons (March-May, October-November) mean muddier trails, but monkeys often stay in smaller, more accessible areas due to abundant bamboo shoots. The equatorial location means temperatures remain fairly constant throughout the year, though mornings can be cool at altitude."
    },
    {
      question: "Should I hire a porter?",
      answer: "Highly recommended! A porter carries your daypack, freeing you to focus on observing monkeys and taking photos without the burden of gear. They also provide physical assistance on steep or slippery sections. Porter hire is affordable and directly supports local communities—many are former poachers who now earn sustainable income from conservation tourism. It's a win-win that enhances your experience while supporting conservation."
    }
  ];

  return (
    <div>
      {/* Hero Section */}
      <section
        className="relative h-96 md:h-[500px] bg-cover bg-center flex items-center"
        style={{
          backgroundImage: 'linear-gradient(rgba(0,0,0,0.4), rgba(0,0,0,0.4)), url(https://images.unsplash.com/photo-1540573133985-87b6da6d54a9?w=1600)',
        }}
      >
        <div className="container mx-auto px-4">
          <div className="max-w-4xl text-white">
            <h1 className="text-3xl md:text-5xl font-bold mb-3 font-heading">
              Golden Monkey Tracking
            </h1>
            <p className="text-xl md:text-2xl mb-6">
              Track rare golden monkeys through bamboo forests on the slopes of ancient volcanoes
            </p>
            <div className="flex items-center gap-6 mb-6">
              <div className="flex items-center gap-2">
                <div className="flex text-safari-gold">
                  {[...Array(5)].map((_, i) => (
                    <FontAwesomeIcon key={i} icon="fa-star" className="w-5 h-5" />
                  ))}
                </div>
                <span className="text-lg">(180+ reviews)</span>
              </div>
            </div>
            <div className="flex flex-wrap gap-4 mb-8">
              <div className="bg-white/90 backdrop-blur-sm text-safari-green px-4 py-2 rounded-full text-sm font-semibold">
                <FontAwesomeIcon icon="fa-shield-halved" className="w-4 h-4 mr-2" />
                UWA Licensed
              </div>
              <div className="bg-white/90 backdrop-blur-sm text-safari-green px-4 py-2 rounded-full text-sm font-semibold">
                <FontAwesomeIcon icon="fa-leaf" className="w-4 h-4 mr-2" />
                Conservation Focused
              </div>
            </div>
            <button
              onClick={() => setIsModalOpen(true)}
              className="bg-safari-gold hover:bg-safari-gold/90 text-white px-8 py-4 rounded-full text-lg font-bold transition-all duration-300 shadow-lg hover:shadow-xl"
            >
              Book Your Trek
            </button>
          </div>
        </div>
      </section>

      {/* Page Content */}
      <div className="container mx-auto px-4 py-16">
        <div className="lg:grid lg:grid-cols-3 lg:gap-12">
          {/* Main Content */}
          <main className="lg:col-span-2">
            {/* Description Section */}
            <section className="mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-safari-green mb-8">
                Meet Africa's Most Playful Primates
              </h2>
              <div className="prose prose-lg max-w-none">
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  Golden monkey tracking in Mgahinga Gorilla National Park offers you an intimate encounter with one of Africa's rarest and most captivating primates. High in the emerald bamboo forests on the slopes of ancient volcanoes, you'll discover troops of golden monkeys—their distinctive golden-orange fur creating brilliant flashes of color against the green canopy as they leap acrobatically from stalk to stalk. These endangered primates are found exclusively in the Virunga Mountains, and with only 2,000 to 5,000 individuals remaining worldwide, your encounter represents both a remarkable privilege and a vital contribution to their conservation.
                </p>
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  This experience offers a completely different dynamic from gorilla trekking. While gorillas move slowly and deliberately through the forest, golden monkeys are energetic acrobats, constantly in motion as they feed, play, groom, and communicate through chirps, clicks, and expressive facial gestures. You'll observe them in their natural bamboo realm at altitudes between 2,100 and 3,550 meters, where they spend most of their time high in the canopy—sometimes 30 meters up—performing aerial stunts that showcase their remarkable agility.
                </p>
                <h3 className="text-2xl font-bold text-safari-green mt-8 mb-4">Understanding Golden Monkeys</h3>
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  Golden monkeys (Cercopithecus kandti) are one of Africa's rarest primates, endemic to the Albertine Rift montane forests. Distinguished by their striking golden-orange backs, black limbs, and expressive faces with golden cheeks, these Old World monkeys live in social troops ranging from 30 to over 100 individuals. Each troop is led by a dominant male, with complex social hierarchies governing their interactions. Their highly social nature means you'll witness constant communication—vocalizations, grooming sessions, playful wrestling among juveniles, and mothers tenderly caring for their infants.
                </p>
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  The bamboo forests they inhabit provide both food and shelter. Golden monkeys feed primarily on bamboo shoots and leaves, especially during the rainy season when young shoots are abundant and nutritious. They also forage for over 100 species of fruits, flowers, and leaves, and occasionally consume insects. Their diet and ranging patterns follow seasonal food availability, making each tracking experience unique. Unlike mountain gorillas, golden monkeys tend to return to the same sleeping sites night after night, weaving bamboo stalks together to create stable sleeping platforms at the tops of bamboo plants.
                </p>
                <h3 className="text-2xl font-bold text-safari-green mt-8 mb-4">Conservation and Your Impact</h3>
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  Golden monkeys are classified as Endangered on the IUCN Red List, facing threats from habitat loss, deforestation, bamboo harvesting, and human-wildlife conflict. The Virunga Massif has experienced significant habitat degradation since the 1950s, with some areas losing 50% of their forest cover. Your tracking permit directly funds conservation efforts including anti-poaching patrols, habitat protection, and community programs that reduce human-wildlife conflict. Tourism has transformed former poachers into porters and guides, creating sustainable livelihoods that incentivize conservation rather than exploitation.
                </p>
                <p className="text-lg text-gray-700 leading-relaxed">
                  The habituation process—gradually teaching wild golden monkeys to tolerate human presence—enables both research and sustainable tourism. Scientists can study their behavior, social structures, and health, while visitors like you gain unforgettable wildlife encounters that fund ongoing protection. Every tracking experience contributes to ensuring these remarkable primates continue to thrive in their mountain bamboo forests for generations to come.
                </p>
              </div>
            </section>

            {/* Highlights */}
            <section className="mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-safari-green mb-12 text-center">
                Why Track Golden Monkeys
              </h2>
              <div className="grid md:grid-cols-2 gap-8">
                {[
                  { icon: 'fa-star', title: 'Rare Endemic Species', description: 'Only 2,000-5,000 golden monkeys remain worldwide, found exclusively in the Virunga Mountains spanning Uganda, Rwanda, and DRC' },
                  { icon: 'fa-face-smile', title: 'Incredibly Playful Behavior', description: 'Watch acrobatic leaps through bamboo canopy, playful wrestling among juveniles, grooming rituals, and constant social interactions' },
                  { icon: 'fa-mountain', title: 'Stunning Volcano Setting', description: 'Track on slopes of three ancient volcanoes (Muhabura, Gahinga, Sabinyo) with breathtaking mountain panoramas and bamboo forest scenery' },
                  { icon: 'fa-person-hiking', title: 'More Accessible Trek', description: 'Generally easier than gorilla trekking with shorter distances, less steep terrain, and higher success rates (90%+)' },
                  { icon: 'fa-camera', title: 'Excellent Photography', description: 'Golden-orange fur against emerald bamboo creates stunning contrasts. Fast-moving subjects provide dynamic action shots' },
                  { icon: 'fa-leaf', title: 'Direct Conservation Impact', description: 'Your permit funds habitat protection, anti-poaching patrols, and community programs supporting endangered species conservation' }
                ].map((highlight, index) => (
                  <div key={index} className="flex gap-4">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 bg-safari-green rounded-full flex items-center justify-center">
                        <FontAwesomeIcon icon={highlight.icon} className="w-6 h-6 text-white" />
                      </div>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-safari-green mb-2">{highlight.title}</h3>
                      <p className="text-gray-700">{highlight.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Itinerary */}
            <section className="mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-safari-green mb-8 text-center">
                Your Trekking Experience
              </h2>
              <div className="space-y-6">
                {[
                  { time: '07:30', title: 'Briefing at Park Headquarters', desc: 'Arrive at Ntebeko Gate for registration and comprehensive orientation. Experienced rangers explain golden monkey behavior, social structure, tracking procedures, and safety guidelines. You\'ll be assigned to your tracking group (maximum 8 people) and receive your walking stick.' },
                  { time: '08:00', title: 'Trek into Bamboo Forest', desc: 'Follow your armed ranger guide into the dense bamboo zone where golden monkeys live. Trackers use GPS data and knowledge of the troop\'s ranging patterns to locate them. The trek duration varies from 30 minutes to 4 hours depending on the monkeys\' location and movements that day.' },
                  { time: '09:30', title: 'Golden Monkey Encounter', desc: 'Spend one magical hour observing the habituated troop. Watch them feed on bamboo shoots, leap acrobatically through the canopy, groom each other, play, and communicate. Juveniles wrestle and chase while mothers care for infants. The monkeys\' constant movement and playful nature create endless fascinating moments.' },
                  { time: '10:30', title: 'Return Trek to Headquarters', desc: 'After your hour with the monkeys, hike back to park headquarters. The return journey is typically quicker as you follow established trails. Enjoy the bamboo forest atmosphere and mountain views as you descend.' },
                  { time: '12:00', title: 'Certificate & Departure', desc: 'Receive your official golden monkey tracking certificate at headquarters—a memorable souvenir of your experience. Head back to your lodge for lunch and relaxation, or continue with other Mgahinga activities if you\'ve planned a multi-day visit.' }
                ].map((step, index) => (
                  <div key={index} className="card">
                    <div className="p-6 flex gap-4">
                      <div className="flex-shrink-0">
                        <div className="w-16 h-16 bg-safari-green rounded-full flex items-center justify-center text-white font-bold text-sm text-center leading-tight p-1">
                          {step.time}
                        </div>
                      </div>
                      <div className="flex-grow">
                        <h3 className="text-xl font-bold text-safari-green mb-2">{step.title}</h3>
                        <p className="text-gray-700">{step.desc}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* What to Bring */}
            <section className="mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-safari-green mb-8 text-center">
                Essential Packing Guide
              </h2>
              <div className="bg-safari-light border-l-4 border-safari-gold p-6 mb-8 rounded-r-lg">
                <p className="text-gray-700 text-lg">
                  <strong>⚠️ Important Note:</strong> This itinerary is tentative and can be customized based on your preferences
                  and pickup location. We can adjust all aspects to suit your needs.
                </p>
              </div>
              <div className="grid md:grid-cols-2 gap-8">
                <div className="card p-6">
                  <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                    <FontAwesomeIcon icon="fa-shirt" className="w-6 h-6" />
                    Clothing Essentials
                  </h3>
                  <ul className="space-y-3 text-gray-700">
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span><strong>Long-sleeved shirt & pants:</strong> Protect against thorns, stinging nettles, and insects. Choose lightweight, quick-dry fabrics in neutral colors (green, brown, khaki).</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span><strong>Waterproof rain jacket:</strong> Mgahinga receives rain year-round. A lightweight, breathable rain jacket is essential for staying dry and comfortable.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span><strong>Warm fleece or sweater:</strong> Temperatures at 2,500m+ altitude can be cool, especially in early morning or if weather changes.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span><strong>Garden gloves:</strong> Protect your hands when grabbing vegetation for support on steep sections or pushing through undergrowth.</span>
                    </li>
                  </ul>
                </div>

                <div className="card p-6">
                  <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                    <FontAwesomeIcon icon="fa-hiking" className="w-6 h-6" />
                    Footwear & Accessories
                  </h3>
                  <ul className="space-y-3 text-gray-700">
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span><strong>Sturdy hiking boots:</strong> Waterproof boots with good ankle support and aggressive tread. The forest floor is muddy, slippery, and uneven.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span><strong>Thick socks:</strong> Long socks to tuck pants into, preventing insects from crawling up your legs.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span><strong>Gaiters (optional):</strong> Keep mud and debris out of your boots during wet conditions.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span><strong>Walking stick:</strong> Provided by the park, but you can bring your own for additional support on steep or slippery sections.</span>
                    </li>
                  </ul>
                </div>

                <div className="card p-6">
                  <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                    <FontAwesomeIcon icon="fa-camera" className="w-6 h-6" />
                    Photography & Gear
                  </h3>
                  <ul className="space-y-3 text-gray-700">
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span><strong>Camera with telephoto lens:</strong> A 70-200mm or 100-400mm lens is ideal for capturing monkeys from the required distance. Bring extra batteries and memory cards.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span><strong>Binoculars:</strong> Essential for observing monkeys high in the canopy. 8x42 or 10x42 magnification works well in forest conditions.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span><strong>Waterproof camera bag:</strong> Protect expensive equipment from rain and humidity in a backpack-style bag.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span><strong>Note:</strong> Flash photography is strictly prohibited as it disturbs the monkeys.</span>
                    </li>
                  </ul>
                </div>

                <div className="card p-6">
                  <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                    <FontAwesomeIcon icon="fa-bottle-water" className="w-6 h-6" />
                    Food & Hydration
                  </h3>
                  <ul className="space-y-3 text-gray-700">
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span><strong>2+ liters of water:</strong> Hydration is critical during several hours of hiking at altitude. Use a hydration bladder or multiple bottles.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span><strong>Energy snacks:</strong> Trail mix, energy bars, chocolate, dried fruit, or nuts for quick energy boosts during the trek.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span><strong>Packed lunch (optional):</strong> For longer treks or if combining with other activities, bring non-perishable foods.</span>
                    </li>
                  </ul>
                </div>

                <div className="card p-6">
                  <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                    <FontAwesomeIcon icon="fa-kit-medical" className="w-6 h-6" />
                    Health & Safety
                  </h3>
                  <ul className="space-y-3 text-gray-700">
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span><strong>Insect repellent (DEET 30%+):</strong> Forest mosquitoes and other insects are persistent. Reapply throughout the trek.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span><strong>Sunscreen & lip balm:</strong> Even in forest shade, UV exposure accumulates over several hours.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span><strong>Personal first-aid kit:</strong> Bandages, blister treatment, pain relievers, and any prescription medications.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span><strong>Face mask & hand sanitizer:</strong> Required for monkey protection. Golden monkeys are susceptible to human diseases.</span>
                    </li>
                  </ul>
                </div>

                <div className="card p-6">
                  <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                    <FontAwesomeIcon icon="fa-circle-plus" className="w-6 h-6" />
                    Optional but Recommended
                  </h3>
                  <ul className="space-y-3 text-gray-700">
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span><strong>Porter hire:</strong> Highly recommended. A porter carries your daypack and provides physical assistance on steep sections. This directly supports local communities.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span><strong>Small daypack:</strong> For carrying water, snacks, camera, and rain gear. Choose one that's comfortable for several hours of hiking.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span><strong>Notebook & pen:</strong> For recording observations, individual monkey identifications, or memorable moments.</span>
                    </li>
                  </ul>
                </div>
              </div>
            </section>

            {/* FAQs */}
            <FAQSection faqs={faqs} title="Your Questions Answered" />

            {/* Related Activities */}
            <section>
              <h2 className="text-3xl md:text-4xl font-bold text-safari-green mb-8 text-center">
                More Mgahinga Adventures
              </h2>
              <div className="grid md:grid-cols-2 gap-8">
                <Link to="/activities/gorilla-trekking" className="card group hover:shadow-xl transition-all duration-300">
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src="https://images.unsplash.com/photo-1576669801775-ff43c5ab543d?w=800"
                      alt="Gorilla Trekking"
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-safari-green mb-2">Gorilla Trekking</h3>
                    <p className="text-gray-700 mb-4">The ultimate wildlife encounter.</p>
                    <p className="text-safari-gold font-bold">Activity fees apply</p>
                  </div>
                </Link>

                <Link to="/activities/mount-sabinyo-climb" className="card group hover:shadow-xl transition-all duration-300">
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src="https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?w=800"
                      alt="Mount Sabinyo"
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-safari-green mb-2">Mount Sabinyo Climb</h3>
                    <p className="text-gray-700 mb-4">Summit three countries at once.</p>
                    <p className="text-safari-gold font-bold">Activity fees apply</p>
                  </div>
                </Link>
              </div>
            </section>
          </main>

          {/* Sidebar */}
          <aside className="lg:col-span-1">
            <div className="sticky top-24">
              <div className="card p-6 mb-8">
                <h3 className="text-2xl font-bold text-safari-green mb-4">Activity Details</h3>
                <div className="space-y-4 text-gray-700">
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon="fa-clock" className="w-6 h-6 text-safari-green" />
                    <span><strong>Duration:</strong> 4-6 hours</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon="fa-calendar" className="w-6 h-6 text-safari-green" />
                    <span><strong>Best Time:</strong> Year-round</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon="fa-dollar-sign" className="w-6 h-6 text-safari-green" />
                    <span><strong>Activity Fee:</strong> Contact for current rates</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon="fa-chart-simple" className="w-6 h-6 text-safari-green" />
                    <span><strong>Difficulty:</strong> Moderate</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon="fa-users" className="w-6 h-6 text-safari-green" />
                    <span><strong>Group:</strong> Max 8 people</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon="fa-user" className="w-6 h-6 text-safari-green" />
                    <span><strong>Min Age:</strong> 12 years</span>
                  </div>
                </div>
                <button
                  onClick={() => setIsModalOpen(true)}
                  className="bg-safari-gold hover:bg-safari-gold/90 text-white px-8 py-4 rounded-full text-lg font-bold transition-all duration-300 shadow-lg hover:shadow-xl w-full mt-6"
                >
                  Book Your Trek
                </button>
              </div>

              <div className="card p-6 mb-8">
                <h3 className="text-2xl font-bold text-safari-green mb-6 flex items-center gap-2">
                  <FontAwesomeIcon icon="fa-check-circle" className="w-6 h-6 text-green-600" />
                  What's Included
                </h3>
                <ul className="space-y-3">
                  {[
                    "Tracking permit (required)",
                    "Park entrance fees",
                    "Armed ranger guide",
                    "Tracker team services",
                    "Security escort",
                    "Tracking certificate"
                  ].map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <FontAwesomeIcon icon="fa-check" className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="card p-6 mb-8">
                <h3 className="text-2xl font-bold text-safari-green mb-6 flex items-center gap-2">
                  <FontAwesomeIcon icon="fa-times-circle" className="w-6 h-6 text-red-600" />
                  What's Not Included
                </h3>
                <ul className="space-y-3">
                  {[
                    "Transport to park",
                    "Porter hire (optional)",
                    "Packed lunch",
                    "Tips for guides",
                    "Accommodation"
                  ].map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <FontAwesomeIcon icon="fa-times" className="w-5 h-5 text-red-600 mt-1 flex-shrink-0" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="card p-6">
                <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                  <FontAwesomeIcon icon="fa-heartbeat" className="w-6 h-6" />
                  Health & Fitness
                </h3>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start gap-2">
                    <FontAwesomeIcon icon="fa-check-circle" className="w-5 h-5 text-safari-green mt-0.5" />
                    <span><strong>Fitness:</strong> Moderate. Some steep sections.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <FontAwesomeIcon icon="fa-check-circle" className="w-5 h-5 text-safari-green mt-0.5" />
                    <span><strong>Health:</strong> No cold/flu allowed (protects monkeys).</span>
                  </li>
                </ul>
              </div>
            </div>
          </aside>
        </div>
      </div>

      <BookingModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        experience="Golden Monkey Tracking"
      />
    </div>
  )
}
